// Cómo saluda el bot · DIRECTRIZ-BOT-DOS-IDENTIDADES Parte B (16 sep 2026)
//
// Los textos con los que el bot recibe a la gente son datos, y aquí se
// editan sin tocar código: el saludo (con {nombre}), la presentación, la
// firma, las respuestas a «hola», «gracias» y «adiós», el «no entendí»
// suave y las palabras que reconoce. Cada campo enseña al lado su valor
// de omisión; dejarlo vacío es volver a él.
//
// VIVE DENTRO DE «Interesados» y no en una pestaña propia: la pestaña es
// del carril `consola` (PanelApp.jsx) y esta pantalla es del bot. Lo que
// el dueño busca —«¿cómo saluda mi bot?»— está donde ya mira a su bot.
import { useCallback, useEffect, useState } from 'react';
import { MessageCircle, RotateCcw, Save } from 'lucide-react';
import { apiPanel } from '../panel/api';

const IDIOMA = { '': 'Todos', es: 'Español', en: 'Inglés' };

function nombreDeClave(clave) {
  return {
    saludo: 'Saludo',
    saludo_sin_nombre: 'Saludo sin nombre',
    momento_manana: 'Por la mañana',
    momento_tarde: 'Por la tarde',
    momento_noche: 'Por la noche',
    presentacion: 'Presentación',
    que_puedo: 'Qué puede hacer (solo la primera vez)',
    firma: 'Firma',
    despues_del_saludo: 'Antes de los botones',
    resp_saludo: 'A un «hola»',
    resp_gracias: 'A un «gracias»',
    resp_despedida: 'A un «adiós»',
    no_entendi: 'Cuando no entiende',
    no_entendi_persona: 'Al segundo tropiezo',
    trabajador_saludo: 'Saludo a un trabajador',
    trabajador_que_puedo: 'Qué puede hacer un trabajador',
    dueno_saludo: 'Saludo al dueño',
    dueno_que_puedo: 'Antes de las opciones de operador',
    ventana_saludo_horas: 'Ventana del saludo (horas)',
    palabras_saludo: 'Palabras que cuentan como saludo',
    palabras_gracias: 'Palabras que cuentan como gracias',
    palabras_despedida: 'Palabras que cuentan como despedida',
    nivel_voz_publico: 'Voz del bot público (sobrio / amigable)',
    nivel_voz_privado: 'Voz del bot privado (sobrio / amigable)',
    boton_planes: 'Botón: ver planes',
    boton_cotizar: 'Botón: cotizar',
    boton_alta: 'Botón: crear cuenta',
    boton_humano: 'Botón: hablar con una persona',
    planes_intro: 'Ver planes: primera línea',
    plan_gratis_beneficio: 'Plan Gratis: beneficio',
    plan_simple_beneficio: 'TEGO Simple: beneficio',
    plan_control_beneficio: 'TEGO Control: beneficio',
    plan_mas_elegido: 'Marca del más elegido',
    planes_cierre: 'Ver planes: cierre',
    cotizar_arranque: 'Cotizar: cómo abre',
    alta_texto: 'Crear cuenta: texto',
    humano_texto: 'Hablar con una persona: texto',
    humano_horario: 'Cuándo escribe el equipo',
    humano_contexto_gracias: 'Al recibir la línea de contexto',
  }[clave] || clave;
}

export default function CortesiaDelBot() {
  const [abierto, setAbierto] = useState(false);
  const [textos, setTextos] = useState([]);
  const [borrador, setBorrador] = useState({});
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [guardado, setGuardado] = useState(false);
  const [idioma, setIdioma] = useState('es');

  const cargar = useCallback(() => {
    setCargando(true);
    apiPanel
      .get('/bot/cortesia')
      .then((lista) => {
        setTextos(lista);
        setBorrador(Object.fromEntries(lista.map((t) => [`${t.clave}|${t.idioma}`, t.valor])));
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    if (!abierto) return undefined;
    const t = setTimeout(cargar, 0);
    return () => clearTimeout(t);
  }, [abierto, cargar]);

  const cambios = textos.filter((t) => (borrador[`${t.clave}|${t.idioma}`] ?? '') !== (t.valor ?? ''));

  const guardar = () => {
    setGuardando(true);
    setGuardado(false);
    apiPanel
      .post('/bot/cortesia', {
        textos: cambios.map((t) => ({
          clave: t.clave,
          idioma: t.idioma,
          valor: borrador[`${t.clave}|${t.idioma}`] ?? '',
        })),
      })
      .then((lista) => {
        setTextos(lista);
        setBorrador(Object.fromEntries(lista.map((t) => [`${t.clave}|${t.idioma}`, t.valor])));
        setError(null);
        setGuardado(true);
      })
      .catch((e) => setError(e.message))
      .finally(() => setGuardando(false));
  };

  const visibles = textos.filter((t) => t.idioma === idioma || t.idioma === '');

  return (
    <section className="mt-6 rounded-xl border bg-white">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="w-full flex items-center gap-3 p-4 text-left"
      >
        <MessageCircle className="h-5 w-5 text-gray-500 shrink-0" />
        <span className="flex-1">
          <span className="block font-semibold text-gray-900">Cómo saluda el bot</span>
          <span className="block text-sm text-gray-500">
            El saludo, la presentación, la firma, lo que contesta a un «hola» o un «gracias», la
            voz de cada bot (sobrio / amigable) y los textos con los que vende. Se cambian aquí,
            sin tocar código.
          </span>
        </span>
        <span className="text-sm text-blue-700">{abierto ? 'Cerrar' : 'Editar'}</span>
      </button>

      {abierto && (
        <div className="border-t p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-gray-600">Idioma que editas:</span>
            {['es', 'en'].map((i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIdioma(i)}
                className={`px-3 py-1.5 rounded-lg border ${
                  idioma === i ? 'bg-gray-900 text-white border-gray-900' : 'hover:bg-gray-50'
                }`}
              >
                {IDIOMA[i]}
              </button>
            ))}
            <span className="text-gray-500">
              El bot habla en el idioma de cada chat; los dos se editan por separado.
            </span>
          </div>

          {cargando && <p className="text-sm text-gray-500">Cargando los textos…</p>}

          {!cargando &&
            visibles.map((t) => {
              const clave = `${t.clave}|${t.idioma}`;
              const valor = borrador[clave] ?? '';
              const largo = t.clave.startsWith('palabras_') || t.clave.startsWith('que_puedo');
              return (
                <div key={clave} className="space-y-1">
                  <label className="block text-sm font-medium text-gray-900" htmlFor={clave}>
                    {nombreDeClave(t.clave)}
                    {t.idioma === '' && (
                      <span className="ml-2 text-xs text-gray-500">(vale para los dos idiomas)</span>
                    )}
                  </label>
                  <p className="text-xs text-gray-500">{t.ayuda}</p>
                  {largo ? (
                    <textarea
                      id={clave}
                      rows={3}
                      value={valor}
                      placeholder={t.omision}
                      onChange={(e) => setBorrador({ ...borrador, [clave]: e.target.value })}
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                    />
                  ) : (
                    <input
                      id={clave}
                      value={valor}
                      placeholder={t.omision}
                      onChange={(e) => setBorrador({ ...borrador, [clave]: e.target.value })}
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                    />
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {valor.trim() ? (
                        <>Si lo dejas vacío, vuelve a: «{t.omision || '(nada)'}»</>
                      ) : (
                        <>Usando el valor de omisión.</>
                      )}
                    </span>
                    {valor.trim() && (
                      <button
                        type="button"
                        onClick={() => setBorrador({ ...borrador, [clave]: '' })}
                        className="inline-flex items-center gap-1 text-blue-700"
                      >
                        <RotateCcw className="h-3 w-3" /> Volver a la omisión
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

          {error && <div className="rounded-lg bg-red-50 text-red-800 text-sm p-3">{error}</div>}
          {guardado && !cambios.length && (
            <div className="rounded-lg bg-green-50 text-green-800 text-sm p-3">
              Guardado. El bot usa los textos nuevos desde el siguiente mensaje que reciba.
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={guardar}
              disabled={!cambios.length || guardando}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
            {!cambios.length && !guardando && (
              <span className="text-sm text-gray-500">
                Guardar se enciende cuando cambies algún texto.
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
